import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import "../../styles/MyProfile.css";

const BlueHandsAvailableNow = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData] = useFetch();
  const userId = authState.user._id;
  const [isAvailable, setIsAvailable] = useState({});

  const [originalProfileData, setOriginalProfileData] = useState({});
  const [profileExists, setProfileExists] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [loading, setLoading] = useState(true);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [createSuccessMsg, setCreateSuccessMsg] = useState('');
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState('');
  const [creatingProfile, setCreatingProfile] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserProfile(userId);
  }, [userId]);

  const [profileData, setProfileData] = useState({
    userId: userId,
    think5whysCode: authState.user.think5whyscode,
    name: authState.user.name,
    isAvailable: true, 
    availableForWork: '',
    availabilityRemarks: '', 
    referredBy: '',
    minAmount: 0,
    maxAmount: 0,
    currency: 'Rs.',
    amtTenure: 'Monthly',
    isNegotiable: false,
    prefTiming: '',
    masterReference: '',
    overtimeChoice: '',
    myPreferredCategory: '',
    myPreferredLocation: '',
    myKeywords: '',
  });

  const fetchUserProfile = useCallback(() => { 
    try {
      const config = {
        url: "/blueprofile/availablebyid",
        method: 'post',
        data: { userId: userId },
        headers: { Authorization: authState.token },
      };
  
      fetchData(config)
        .then(response => {
          if (response && response.status) {
            // Profile exists
            setProfileExists(true);
            setIsAvailable(response.blueProfile.isAvailable); 
            setProfileData({
              isAvailable: response.blueProfile.isAvailable, 
              availableForWork: response.blueProfile.availableForWork,
              availabilityRemarks: response.blueProfile.availabilityRemarks, 
              referredBy: response.blueProfile.referredBy,
              minAmount: response.blueProfile.minAmount,
              maxAmount: response.blueProfile.maxAmount,
              currency: response.blueProfile.currency,
              amtTenure: response.blueProfile.amtTenure,
              isNegotiable: response.blueProfile.isNegotiable,
              prefTiming: response.blueProfile.prefTiming,
              masterReference: response.blueProfile.masterReference,
              overtimeChoice: response.blueProfile.overtimeChoice,
              myPreferredCategory: response.blueProfile.myPreferredCategory,
              myPreferredLocation: response.blueProfile.myPreferredLocation,
              myKeywords: response.blueProfile.myKeywords,
            });
          } else {
            setCreateSuccessMsg("Sorry! Profile Not Found. Please retry."); 
          }
        })
        .catch(() => {
          setCreateSuccessMsg("Sorry! Profile Not Found. Please retry."); 
        });
    } catch (error) {
      setCreateSuccessMsg("Sorry! An unexpected error occurred. Please retry."); 
    }
  }, [userId, authState.token, fetchData, setProfileExists, setIsAvailable, setProfileData, setCreateSuccessMsg]);
  

  const handleAvailabilityUpdate = useCallback(() => {
    const config = {
      url: `/blueprofile/updateavailability`,
      method: 'post',
      data: { profileData: profileData, userId: userId},
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(response => {
        if (response && response.status) {
          setUpdateSuccess(true);
          setUpdateSuccessMsg("Availability Updated successfully!");
        } else {
          setUpdateSuccessMsg("Error updating! Please retry.");
        }
      })
      .catch(() => {
        setUpdateSuccessMsg("Error updating! Please retry.");
      });
  }, [isAvailable, profileId, fetchData]);

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <MainLayout>
      <div className="text-lg mt-12 mb-4 mx-8">
        <h2 className="text-2xl font-bold text-white text-center mb-6">Blue Profile</h2>
      </div>
  
      <div className="min-h-[50vh] mt-4 border border-b-gray-300 p-6 rounded-lg bg-gray-800 text-white">
        {!profileExists ? (
          <div className="flex justify-center items-center mt-4">
            <button
              className="create-profile-button px-4 py-2 bg-blue-600 rounded-full text-white hover:bg-blue-700"
              onClick={handleCancel}
            >
              Your Blue Profile Does Not Exist!!
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <label className="text-yellow-200">Name:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="text"
                  value={authState.user.name}
                  disabled
                />
              </div>
            </div>

            <div className='flex flex-col'>
              <label>Availability Remarks:</label>
              <textarea
                className="bg-gray-600 rounded-md px-2 py-1"
                style={{ border: '1px solid #ccc', maxWidth: '100%', maxHeight: '150px' }}
                value={profileData.availabilityRemarks}
                onChange={(e) => setProfileData({ ...profileData, availabilityRemarks: e.target.value })}
              />
            </div>

            <div className='flex flex-col'>
              <label>Minimum Expectation:</label>
              <input
                className="profile-input bg-gray-600 rounded-md px-2 py-1"
                type="text"
                value={profileData.minAmount}
                style={{ width: '150px' }}
                onChange={(e) => setProfileData({ ...profileData, minAmount: e.target.value })}
              />
            </div>
            <div className='flex flex-col'>
              <label>Maximum Expectation:</label>
              <input
                className="profile-input bg-gray-600 rounded-md px-2 py-1"
                type="text"
                value={profileData.maxAmount}
                style={{ width: '150px' }}
                onChange={(e) => setProfileData({ ...profileData, maxAmount: e.target.value })}
              />
            </div>
            <div className='flex flex-col'>
              <label>Currency:</label>
              <input
                className="profile-input bg-gray-600 rounded-md px-2 py-1"
                type="text"
                value={profileData.currency}
                style={{ width: '50px' }}
                onChange={(e) => setProfileData({ ...profileData, currency: e.target.value })}
              />
            </div>
            <div className='flex flex-col'>
              <label>For a Period:</label>
              <input
                className="profile-input bg-gray-600 rounded-md px-2 py-1"
                type="text"
                value={profileData.amtTenure}
                style={{ width: '200px' }}
                onChange={(e) => setProfileData({ ...profileData, amtTenure: e.target.value })}
              />
            </div>
            <div className='flex flex-col'>
              <label>Time Preference & Time Zone:</label>
              <input
                className="profile-input bg-gray-600 rounded-md px-2 py-1"
                type="text"
                value={profileData.prefTiming}
                style={{ width: '300px' }}
                onChange={(e) => setProfileData({ ...profileData, prefTiming: e.target.value })}
              />
            </div>
            <div className='flex flex-col'>
                <label>Overtime Choice:</label>
                <select
                  className="profile-select bg-gray-600 rounded-md px-2 py-1"
                  style={{ width: '300px' }}
                  value={profileData.overtimeChoice}
                  onChange={(e) => setProfileData({ ...profileData, overtimeChoice: e.target.value })}
                >
                  <option value="Yes - Max 2 Hrs">Yes - Max 2 Hrs</option>
                  <option value="Yes - Max 4 Hrs">Yes - Max 4 Hrs</option>
                  <option value="No">Not Interested in Overtime</option>
                </select>
              </div>
              <div className='flex flex-col'>
                <label>Preferred Job Location:</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="text"
                  value={profileData.myPreferredLocation}
                  style={{ width: '300px' }}
                  onChange={(e) => setProfileData({ ...profileData, myPreferredLocation: e.target.value })}
                />
              </div>
              <div className='flex flex-col'>
                <label>Prferred Category:</label>
                <select
                  value={profileData.myPreferredCategory}
                  onChange={(e) => setProfileData({ ...profileData, myPreferredCategory: e.target.value })}
                  className="border border-gray-300 bg-gray-600  rounded-md px-4 py-2 w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ border: '1px solid #ccc', padding: '8px', height: '36px', width: '200px' }}
                >
                  <option value="Blue Collar">Highly Skilled</option>
                  <option value="Labour">Labour</option>
                  <option value="Contractor">Contractor</option>
                  <option value="Consultant">Consultant</option>
                  <option value="Company">Jobs</option>
                  <option value="Supervisor">Supervisor</option>
                </select>
              </div>
              <div className='flex flex-col'>
                <label>Keywords for me (Max 5):</label>
                <input
                  className="profile-input bg-gray-600 rounded-md px-2 py-1"
                  type="text"
                  value={profileData.myKeywords}
                  style={{ width: '100%' }}
                  onChange={(e) => setProfileData({ ...profileData, myKeywords: e.target.value })}
                />
                {errors.myKeywords && <span className="text-red-500 text-xs">{errors.myKeywords}</span>}
              </div>
              <div className='flex flex-col text-xl '>
                <label className='text-yellow-300'>
                  <input 
                    className="bg-gray-600 rounded-md px-2 py-1 mr-4 " 
                    type="checkbox" 
                    checked={profileData.isAvailable} 
                    onChange={(e) => setProfileData({ ...profileData, isAvailable: e.target.checked })} 
                  />
                  I am Available
                </label>
              </div>
              {updateSuccess && (
                <div className="success-message text-green-500 text-lg mt-4">
                  {updateSuccessMsg}
                </div>
              )}
  
            <div className="flex mt-6 gap-4">
              <button
                className="update-profile-button px-4 py-2 bg-green-600 rounded-full text-white hover:bg-green-700 w-40"
                onClick={handleAvailabilityUpdate}
              >
                Update Availability
              </button>
              <button
                className="update-profile-button px-4 py-2 bg-red-600 rounded-full text-white hover:bg-red-700 w-40"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default BlueHandsAvailableNow;



